import NavigationArrowIcon from "@/assets/icons/NavigationArrowIcon";
import StarToggle from "@/components/common/StarToggle";
import { useCanvas as useCanvasContext } from "@/hooks/useCanvas";
import { useVotingStatus } from "@/hooks/useVotingStatus";
import { routes } from "@/routes/paths";
import { TopicId, usePriorityStore } from "@/stores/priorityStore";
import { Box, Group, Text, Textarea, Stack, Divider, Title } from "@mantine/core";
import { memo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { brandColorTokens } from "@canucci/base-mantine/theme";

interface PriorityItemProps {
  topicId: TopicId;
  isLast: boolean;
}

function isCustomerTemporaryOverride2603(organizationName: string): boolean {
  return organizationName.toLowerCase().includes("saab");
}

/**
 * Renders a single item in the priorities modal list.
 * This component is memoized and uses granular selectors to prevent
 * unnecessary re-renders when other items are updated.
 */
function PriorityItemComponent({ topicId, isLast }: PriorityItemProps) {
  // Use granular selectors for performance.
  const topic = usePriorityStore((state) => state.selectedTopics[topicId]);
  const comment = usePriorityStore((state) => state.comments[topicId] || "");
  const { removeTopic, setComment, closeModal } = usePriorityStore.getState();

  const navigate = useNavigate();
  const { canvasId } = useParams<{ canvasId: string }>();
  const { isParticipation, canvasResult } = useCanvasContext();
  const canvasData = canvasResult.data;
  const organizationName = canvasData?.meta?.organization_name;
  const { isVotingClosed } = useVotingStatus();
  const isSaab = isCustomerTemporaryOverride2603(organizationName ?? "");
  const { t: tCommon } = useTranslation("common");
  const commentPlaceholder = tCommon(
    isSaab ? "priorities.modal.saab.commentPlaceholder" : "priorities.modal.commentPlaceholder",
    {
      defaultValue: "What measurable goal should we set for this action?",
    },
  );
  const addComment = tCommon(isSaab ? "priorities.modal.saab.addComment" : "priorities.modal.addComment", {
    defaultValue: "Add a measurable goal",
  });

  const [isCommentExpanded, setIsCommentExpanded] = useState(comment.length > 0);

  if (!topic) return null;

  const handleNavigateToTopic = (event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!canvasId) return;
    const path = routes.canvas.focusArea.path(canvasId, topic.groupIndex, topic.areaIndex, isParticipation);
    closeModal();
    navigate(path, { viewTransition: true });
  };

  return (
    <Box>
      <Stack align="stretch" gap="xs" py="xs">
        <Group gap="xs" wrap="nowrap">
          <Box style={{ flexShrink: 0 }}>
            <StarToggle
              isSelected={true}
              onToggle={() => {
                if (isVotingClosed) return;
                removeTopic(topicId);
              }}
              size="md"
              color={brandColorTokens.slideTextOnDark}
              disabled={isVotingClosed}
            />
          </Box>

          <Stack
            align="flex-start"
            gap={0}
            flex={1}
            // 🔒 EXCEPTION: Interaction
            style={{ cursor: "pointer" }}
            onClick={handleNavigateToTopic}
          >
            <Title fw={700} mb="xs" size="h4" c="var(--text-on-dark)">
              {topic.title}
            </Title>
            <Text fz="sm" c="var(--text-on-dark-muted)">
              {topic.label}
            </Text>
          </Stack>

          <Box style={{ flexShrink: 0, cursor: "pointer" }} onClick={handleNavigateToTopic}>
            <NavigationArrowIcon direction="right" color={brandColorTokens.slideTextOnDark} />
          </Box>
        </Group>

        {/* Comment section with corrected layout */}
        <Box pl="md" pr="md" mt="sm">
          {isCommentExpanded ? (
            <Textarea
              placeholder={commentPlaceholder}
              value={comment}
              onChange={(e) => {
                if (isVotingClosed) return;
                setComment(topicId, e.target.value);
              }}
              size="sm"
              rows={2}
              resize="none"
              disabled={isVotingClosed}
            />
          ) : (
            <Text
              fz="sm"
              c="var(--text-on-dark-muted)"
              style={{ cursor: "pointer" }}
              onClick={() => {
                if (isVotingClosed) return;
                setIsCommentExpanded(true);
              }}
            >
              + {addComment}
            </Text>
          )}
        </Box>
      </Stack>
      {!isLast && <Divider my="xs" />}
    </Box>
  );
}

export const PriorityItem = memo(PriorityItemComponent);
